/**
 * The nurse joining bonuses: what they pay, and the one deadline both follow.
 *
 * Nir's rules (2026-09-25), for every nurse who signs up from now on:
 *
 *   $25:  finish onboarding by the deadline.
 *   $150: complete 10 visits by the deadline.
 *
 * The deadline is counted from the day she STARTED her application, on the New
 * York calendar: the last day of that month, unless she started inside the
 * month's last 10 days, in which case it is the last day of the NEXT month. The
 * whole deadline day counts.
 *
 * Pure, no imports: the API awards and pays by it, and the landing page
 * promises it, so both must read the same function or the promise and the
 * payout drift apart (they already did once, when the page carried its own
 * 5-day/14-day variant).
 *
 * Every date here is a `YYYY-MM-DD` key on the New York calendar, handled as
 * integers: never a `Date` added to or compared, so no server zone or DST
 * transition can move a deadline.
 */
export const RN_BONUS_AMOUNT_CENTS = {
    onboarding: 2500,
    ten_visits: 15000
};
/** What a bonus is called wherever it is shown: the app, admin, the payroll CSV, statements. */
export const RN_BONUS_LABELS = {
    onboarding: "Onboarding bonus",
    ten_visits: "10-visit bonus"
};
/** Completed visits the $150 needs. */
export const RN_BONUS_VISITS_REQUIRED = 10;
/** Starting inside the month's last N days moves the deadline to the end of the next month. */
export const RN_BONUS_TAIL_DAYS = 10;
function parseKey(key) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
    if (!match) {
        throw new Error(`Expected a YYYY-MM-DD date, got "${key}"`);
    }
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}
/** Day 0 of the following month is the last day of this one. `month` is 1-12. */
function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
function key(year, month, day) {
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
/** The bonus deadline for an application started on `appliedOn` (`YYYY-MM-DD`, NY). */
export function rnBonusDeadline(appliedOn) {
    const { year, month, day } = parseKey(appliedOn);
    const inTail = day > daysInMonth(year, month) - RN_BONUS_TAIL_DAYS;
    if (!inTail) {
        return key(year, month, daysInMonth(year, month));
    }
    const nextYear = month === 12 ? year + 1 : year;
    const nextMonth = month === 12 ? 1 : month + 1;
    return key(nextYear, nextMonth, daysInMonth(nextYear, nextMonth));
}
/** Whole calendar days from `fromKey` to `toKey` (both `YYYY-MM-DD`); negative when past. */
export function rnBonusDaysBetween(fromKey, toKey) {
    const a = parseKey(fromKey);
    const b = parseKey(toKey);
    return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86_400_000);
}
/** Did something that happened on `doneOn` beat the deadline? Both `YYYY-MM-DD`, NY. */
export function rnBonusDeadlineMet(appliedOn, doneOn) {
    parseKey(doneOn);
    // Zero-padded ISO keys sort as strings.
    return doneOn <= rnBonusDeadline(appliedOn);
}
//# sourceMappingURL=rn-bonus-rules.js.map